import React from "react";
import Select, { components } from "react-select";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import CancelIcon from "@material-ui/icons/Cancel";
import { emphasize } from "@material-ui/core/styles/colorManipulator";
import { getIn } from "formik";
import uniqBy from "lodash-es/uniqBy";
/* eslint-disable react/prop-types, react/jsx-handler-names */

import PropTypes from "prop-types";

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    height: 250,
  },
  input: {
    display: "flex",
    height: "100%",
    padding: "10px 14px !important",
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: "absolute",
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: "absolute",
    zIndex: 2,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
});

class IntegrationReactSelect extends React.Component {
  state = {
    single: null,
    multi: null,
    options: [],
  };

  handleChange = (name) => (value) => {
    this.setState({
      [name]: value,
    });

    const { name: targetName, onChange } = this.props;
    const { value: realValue } = value;
    if (onChange) {
      this.props.onChange({
        target: { name: targetName, value: realValue },
      });
    }
  };

  processSelectedValues = (props) => {
    const { isMulti, value = "" } = props;
    const { options } = this.state;
    const processedValue =
      typeof value === "string"
        ? value
        : Array.isArray(value)
        ? value.map((res) => res.value).join(",")
        : `${value}`;
    const values = new Set((processedValue || "").split(","));
    const selection = options.filter((option) => values.has(`${option.value}`));
    if (isMulti) return selection;
    return selection[0];
  };

  componentDidMount = () => {
    this.fetchOptions();
  };

  needsUpdate = (prevProps) => {
    if (!prevProps) {
      return {
        updateOptions: !!this.props.options,
        updateValues: !!this.props.value,
      };
    }
    return {
      updateValue:
        prevProps && prevProps.value !== this.props.value && this.props.value,
      updateOptions:
        prevProps &&
        prevProps.options !== this.props.options &&
        this.props.options,
    };
  };

  fetchOptions = async (prevProps) => {
    const { updateValue, updateOptions } = this.needsUpdate(prevProps);
    const { isMulti, async: needsFetch, options } = this.props;
    if (updateOptions) {
      const newOptions = needsFetch ? await options() : options;
      return this.setState({ options: newOptions }, () => {
        const selectedValues = this.processSelectedValues(this.props);
        const valueSelector = isMulti ? "multi" : "single";
        if (selectedValues) {
          this.setState({ [valueSelector]: selectedValues });
        }
      });
    }

    if (updateValue) {
      const selectedValues = this.processSelectedValues(this.props);
      const valueSelector = isMulti ? "multi" : "single";
      if (selectedValues) {
        return this.setState({ [valueSelector]: selectedValues });
      }
    }
    return false;
  };

  componentDidUpdate = (prevProps) => {
    this.fetchOptions(prevProps);
  };

  render() {
    const { classes, theme } = this.props;

    const selectStyles = {
      input: (base) => ({
        ...base,
        color: theme.palette.text.primary,
        "& input": {
          font: "inherit",
        },
      }),
    };

    const valueSelector = this.props.isMulti ? "multi" : "single";

    const hasValue = (props, state) => {
      const value = props.isMulti ? state.multi : state.single;

      return value && props.isMulti ? !!value.length : !!value;
    };

    return (
      <Select
        cacheOptions
        defaultOptions
        classes={classes || {}}
        styles={selectStyles}
        isMulti={this.props.isMulti}
        limit={this.props.limit}
        textFieldProps={{
          label: this.props.label,
          hasValue: hasValue(this.props, this.state),
          /* error: showError, */
          /* helperText: showError ? fieldError : this.props.helperText */
        }}
        name={this.props.name}
        isSearchable
        options={this.state.options}
        onChange={this.handleChange(valueSelector)}
        onBlur={this.handleBlur}
        value={this.state[valueSelector]}
        isClearable
        placeholder=""
        components={materialComponents}
        onInputChange={this.props.search ? this.handleInputChange : undefined}
      />
    );
  }
}

IntegrationReactSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

export const MaterialSelectNoFormik = withStyles(styles, { withTheme: true })(
  IntegrationReactSelect
);

function NoOptionsMessage(props) {
  return (
    <Typography
      variant="body2"
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  const { inputValue } = props.selectProps;
  return (
    <TextField
      fullWidth
      variant={
        props.selectProps.textFieldProps.hasValue ? "filled" : "outlined"
      }
      margin={inputValue.margin}
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      InputLabelProps={{
        shrink:
          props.selectProps.textFieldProps.hasValue ||
          (inputValue && inputValue.length),
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      variant="body2"
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
      variant="body2"
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function MenuList(props) {
  const { limit } = props.selectProps ? props.selectProps : {};
  const { children } = props;
  const rChildren = React.Children.toArray(children);
  const lChildren = limit ? rChildren.slice(0, limit) : rChildren;

  return <components.MenuList {...props}>{lChildren}</components.MenuList>;
}

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

const materialComponents = {
  Control,
  Menu,
  MenuList,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

class MaterialSelectInner extends React.Component {
  state = {
    options: [],
    values: [],
  };

  selectToString(value) {
    if (!value) {
      return "";
    }
    return typeof value === "string"
      ? value
      : Array.isArray(value)
      ? value.map((res) => res.value).join(",")
      : `${value.value}`;
  }

  handleChange(newValue) {
    const { onChange } = this.props;
    const { name } = this.props.field || this.props;
    this.setState({ values: newValue });
    const stringValue = this.selectToString(newValue);
    onChange(name, stringValue);
    this.selected = stringValue;
    setTimeout(() => this.getResults(stringValue), 100);
  }

  handleBlur() {
    const { onBlur } = this.props;
    const { name } = this.props.field || this.props;
    onBlur(name, true);
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.optionsFetcher !== this.props.optionsFetcher) {
      this.getResults(this.state.values);
      return;
    }

    if (prevProps.value !== this.props.value) {
      this.getResults(this.props.value);
      return;
    }
  };

  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.getResults = this.getResults.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.selected = undefined;
  }

  getOptions(input) {
    if (this.props.optionsFetcher) {
      return this.props.optionsFetcher(input);
    }
    if (this.state.options.length) {
      return Promise.resolve(this.state.options);
    }
    return Promise.resolve(this.props.options);
  }

  getResults(input) {
    const props = this.props;
    const { options: currentOptions } = this.state;
    return this.getOptions(input).then((result) => {
      const options = result;
      const value = !isNaN(input) ? "" + input : "" + props.value;
      const separatedValues = new Set(
        value.split(",").map((v) => {
          if (isNaN(+v)) {
            return v;
          }
          return +v;
        })
      );
      const alreadyAdded = new Set();
      const cachedCurrentOptions = this.props.cacheOptions
        ? currentOptions
        : currentOptions.filter((option) => separatedValues.has(option.value));
      const values = [...cachedCurrentOptions, ...options].filter((option) => {
        if (alreadyAdded.has(option.value)) {
          return false;
        }
        alreadyAdded.add(option.value);
        return separatedValues.has(option.value);
      });
      const newOptions = uniqBy([...options, ...cachedCurrentOptions], "value");
      this.setState({ options: newOptions, values });
    });
  }

  componentDidMount() {
    const { value } = this.props;
    return this.getResults(value);
  }

  handleInputChange(inputValue) {
    if (!inputValue) {
      return inputValue;
    } else {
      this.selected = undefined;
      this.getResults(inputValue);
    }
    return inputValue;
  }

  render() {
    const { theme, classes } = this.props;

    const selectStyles = {
      input: (base) => ({
        ...base,
        color: theme.palette.text.primary,
        "& input": {
          font: "inherit",
        },
      }),
    };

    const { errors, touched } = this.props.form || {};
    const { name } = this.props.field || {};
    const fieldError = getIn(errors, name);
    const showError = getIn(touched, name) && !!fieldError;

    return (
      <Select
        cacheOptions
        defaultOptions
        classes={classes || {}}
        styles={selectStyles}
        isMulti={this.props.isMulti}
        margin={this.props.noMargin || "normal"}
        limit={this.props.limit}
        textFieldProps={{
          label: this.props.label,
          hasValue: this.state.values && this.state.values.length > 0,
          error: showError,
          helperText: showError ? fieldError : this.props.helperText,
        }}
        name={this.props.name}
        isSearchable
        options={this.state.options}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        value={this.state.values}
        isClearable
        placeholder=""
        components={materialComponents}
        maxMenuHeight={this.props.maxMenuHeight}
        onInputChange={this.props.search ? this.handleInputChange : undefined}
      />
    );
  }
}

export const MaterialSelectFormik = withStyles(styles, { withTheme: true })(
  MaterialSelectInner
);
